import React, { useState } from "react";
import * as csv from "fast-csv";
import { fetchSpecialistFillRateReport } from "../../../../services/report";
import useReportData from "../useReportData";
import Button from "components/Shared/Button";
import TextInputField from "../../../Shared/TextInputField";
import moment from "moment/moment";

const getValidationError = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return "";
  }
  if (startDate > endDate) {
    return "The start date can not be date after end date!";
  }
  const startMoment = moment(startDate, "YYYY-MM-DD");
  const endMoment = moment(endDate, "YYYY-MM-DD");
  if (startMoment.isBefore(moment().subtract(1, "year")) || endMoment.isAfter(moment().add(1, "year"))) {
    return "Date range must be within 1 year before and 1 year after the current date";
  }
  return "";
};

const SpecialistFillRateReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const validationError = getValidationError(startDate, endDate);
  const isReady = Boolean(startDate) && Boolean(endDate) && !validationError;

  const { isLoading, failureReason, info, isEmpty, scheduleLoad, loadNow, download } = useReportData({
    cacheKey: `${startDate}~${endDate}`,
    isReady,
    fileName: "specialist_fill_rate_report.csv",
    infoMessage: `Preparing report(${startDate} - ${endDate})...`,
    emptyMessage: "No available data in given range",
    loadCsv: async () => {
      const columnsNamingMap = {
        specialistName: "Specialist Name",
        specialty: "Specialty",
        date: "Date",
        day: "Day",
        availableBlocks: "Available Blocks",
        totalAvailable: "Total Available",
        taken: "Taken",
        fillRate: "Fill Rate - Total taken / (Total avail + Total taken)",
        timeForNew: "Time for new",
        timeForReview: "Time for review",
        newTaken: "Total new taken",
        reviewTaken: "Total review taken",
        newRemaining: "New Remaining",
        reviewRemaining: "Review Remaining",
      };
      const { data: reportRawData } = await fetchSpecialistFillRateReport({ startDate, endDate });
      if (!reportRawData?.length) {
        return { rowCount: 0 };
      }
      const rows = [Object.keys(reportRawData[0]).map((key) => columnsNamingMap[key] || key)].concat(
        reportRawData.map((data) => Object.values(data).map((a) => (!a ? "" : String(a))))
      );
      return { csv: await csv.writeToString(rows), rowCount: reportRawData.length };
    },
  });

  return (
    <>
      <p className="menu-label">Specialist Fill Rate Report</p>

      <div className="columns">
        <div className="column is-one-quarter">
          <TextInputField
            label="Start date"
            isRequired={true}
            hasError={false}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              scheduleLoad();
            }}
            onBlur={loadNow}
            placeholder={moment().format("YYYY-MM-DD")}
            type="date"
          />
        </div>
        <div className="column is-one-quarter">
          <TextInputField
            label="End date"
            isRequired={true}
            hasError={false}
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              scheduleLoad();
            }}
            onBlur={loadNow}
            placeholder={moment().format("YYYY-MM-DD")}
            type="date"
          />
        </div>
      </div>

      <Button
        isFullWidth={false}
        disabled={!isReady || isLoading || isEmpty}
        isLoading={isLoading}
        onClick={download}
        text="Download"
      />
      <p className="has-text-danger is-size-7">{validationError || failureReason}</p>
      <p className="has-text-info is-size-7">{info}</p>
    </>
  );
};

export default SpecialistFillRateReport;
